import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cn } from '@/utils/cn';
import styles from './Chip.module.scss';
import type { ChipGroupProps, ChipGroupRef } from './ChipGroup.types';

/**
 * ChipGroup - Root for selectable chip groups
 * Wraps Radix UI Toggle Group. Soporta dos modos:
 *   - `selectionMode="single"` (default) — value: string
 *   - `selectionMode="multiple"`         — value: string[]
 * En ambos casos Radix provee roving tabindex, navegación por teclado y
 * accesibilidad completa.
 *
 * @example single
 * <ChipGroup value={filter} onValueChange={setFilter} aria-label="Filter by type">
 *   <Chip value="all">All types</Chip>
 *   <Chip value="grammar">Grammar</Chip>
 * </ChipGroup>
 *
 * @example multiple
 * <ChipGroup selectionMode="multiple" value={selected} onValueChange={setSelected} aria-label="Interests">
 *   <Chip value="business">Business</Chip>
 *   <Chip value="travel">Travel</Chip>
 * </ChipGroup>
 */
export const ChipGroup = React.forwardRef<ChipGroupRef, ChipGroupProps>((props, ref) => {
  const {
    disabled = false,
    scrollable = false,
    className,
    children,
    'aria-label': ariaLabel,
    ...rest
  } = props;

  // El narrowing de la discriminated union vive en cada rama. Radix exige
  // que `type`, `value`, `onValueChange` y `defaultValue` coincidan.
  if (props.selectionMode === 'multiple') {
    const { selectionMode: _ignored, value, onValueChange, defaultValue, ...passthrough } =
      rest as Extract<ChipGroupProps, { selectionMode: 'multiple' }>;
    void _ignored;
    return (
      <ToggleGroupPrimitive.Root
        ref={ref}
        type="multiple"
        value={value}
        onValueChange={onValueChange}
        defaultValue={defaultValue}
        disabled={disabled}
        className={cn(styles.chipGroup, scrollable && styles['chipGroup--scrollable'], className)}
        aria-label={ariaLabel}
        {...passthrough}
      >
        {children}
      </ToggleGroupPrimitive.Root>
    );
  }

  const { selectionMode: _ignored, value, onValueChange, defaultValue, ...passthrough } =
    rest as Extract<ChipGroupProps, { selectionMode?: 'single' }>;
  void _ignored;
  return (
    <ToggleGroupPrimitive.Root
      ref={ref}
      type="single"
      value={value}
      onValueChange={onValueChange}
      defaultValue={defaultValue}
      disabled={disabled}
      className={cn(styles.chipGroup, scrollable && styles['chipGroup--scrollable'], className)}
      aria-label={ariaLabel}
      {...passthrough}
    >
      {children}
    </ToggleGroupPrimitive.Root>
  );
});

ChipGroup.displayName = 'ChipGroup';
